import { FC } from 'react';
import { DetailModal } from 'voyager';

import { ContentParser } from '@common/components/ContentParser';
import useDetailModalLabels from '@common/hooks/useDetailModalLabels';
import useRoomCapacityText from '@common/hooks/useRoomCapacityText';
import type { Room } from '@features/rooms/pages/Rooms/useRooms/types';
import { useTranslations } from '@lang';

import { useStaticRoom } from '../hooks';
import { buildRoomGallery } from './buildRoomGallery';

type AlternativeRoomDetailModalProps = {
  room: Room;
  language?: string;
  fallbackImageUrl?: string;
  onClose: () => void;
  onViewRates: () => void;
};

export const AlternativeRoomDetailModal: FC<AlternativeRoomDetailModalProps> = ({
  room,
  language,
  fallbackImageUrl,
  onClose,
  onViewRates,
}) => {
  const { t } = useTranslations();
  const detailModalLabels = useDetailModalLabels();
  const { staticRoom } = useStaticRoom(room.id, language);

  const title = staticRoom.title || room.title;
  const imageUrls = staticRoom.images.length
    ? staticRoom.images.map((image) => image.url)
    : [room.imageUrl];
  const capacityText = useRoomCapacityText({
    maxAdults: room.maxAdults,
    maxChildren: room.maxChildren,
    maxOccupancy: room.maxOccupancy,
  });

  return (
    <DetailModal
      open
      onClose={onClose}
      title={title}
      images={buildRoomGallery(imageUrls, title, fallbackImageUrl)}
      tags={capacityText ? [capacityText] : undefined}
      pricePerStay={room.price}
      description={
        staticRoom.description ? (
          <ContentParser text={staticRoom.description} escapeHtml={false} />
        ) : undefined
      }
      amenities={(staticRoom.amenities ?? []).map((amenity) => ({
        id: amenity,
        label: amenity,
      }))}
      onReserve={onViewRates}
      locale={language}
      labels={{
        ...detailModalLabels,
        fromPrice: t('common.from'),
        reserve: t('room.viewRates'),
      }}
    />
  );
};
